import { Flag } from "lucide-react";

import { AsideSection } from "../misc/AsideSection";
import type { Company } from "../types";

// LDF: Botschaft – Nationalfeiertag, Flaggenfarben (für länderspezifische Sträuße) und Nationalblume.

// Link zur Wikipedia-Seite der Blume (botanischer Name in Klammern bevorzugt), mehrere Blumen mit „;“ getrennt
const flowerHref = (part: string) => {
  const botanical = part.match(/\(([^)]+)\)/)?.[1];
  const name = (botanical ?? part.replace(/^[^:]+:\s*/, "").split("/")[0]).trim();
  return `https://de.wikipedia.org/w/index.php?search=${encodeURIComponent(name)}`;
};

const PLANTS_BASE = `${import.meta.env.VITE_SUPABASE_URL}/storage/v1/object/public/plants`;

const FlowerLinks = ({ text, label }: { text: string; label?: string }) => (
  <span>
    {text.split(";").map((part, i, all) => (
      <span key={part}>
        <a
          href={flowerHref(part)}
          target="_blank"
          rel="noopener noreferrer"
          className="underline hover:no-underline"
          title="Bild und Beschreibung ansehen"
        >
          {label ? `${label}${text.split(";").length > 1 ? ` ${i + 1}` : ""}` : part.trim()}
        </a>
        {i < all.length - 1 ? "; " : ""}
      </span>
    ))}
  </span>
);

export const CompanyEmbassy = ({ record }: { record: Company }) => {
  if (!record.embassy_country) return null;
  const colours = record.flag_colours ?? [];
  const day = record.national_day?.match(/^(\d{2})-(\d{2})$/);
  return (
    <AsideSection title="Botschaft">
      <div className="flex items-center gap-2 text-sm">
        <Flag className="h-4 w-4 shrink-0" />
        <span className="font-medium">{record.embassy_country}</span>
      </div>
      {day && (
        <div className="text-sm">
          <span className="block text-xs text-muted-foreground">Nationalfeiertag</span>
          {day[2]}.{day[1]}.{record.national_day_name ? ` – ${record.national_day_name}` : ""}
        </div>
      )}
      {colours.length > 0 && (
        <div className="text-sm">
          <span className="block text-xs text-muted-foreground">Flaggenfarben</span>
          <div className="mt-1 flex flex-wrap gap-2">
            {colours.map((c) => (
              <span key={`${c.name_de}-${c.hex}`} className="inline-flex items-center gap-1 text-xs">
                <span className="h-4 w-4 rounded-full border border-border" style={{ backgroundColor: c.hex }} title={c.hex} />
                {c.name_de}
              </span>
            ))}
          </div>
        </div>
      )}
      {record.national_flower && (
        <div className="text-sm">
          <span className="block text-xs text-muted-foreground">Nationalblume</span>
          <span>{record.national_flower}</span>
          {record.national_flower_plants?.length ? (
            <div className="mt-2">
              <span className="block text-xs text-muted-foreground">Aus unserer Blumenbibliothek</span>
              <div className="mt-1 grid grid-cols-3 gap-2">
                {record.national_flower_plants.map((p) => (
                  <a
                    key={p.slug}
                    href={`https://lovelydayflowers.de/botanik/${p.slug}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group block text-center"
                    title={p.kind === "exakt" ? p.name : `${p.name} (gleiche Gattung, nicht exakt die Nationalblume)`}
                  >
                    {p.thumb && (
                      <img
                        src={`${PLANTS_BASE}/${p.thumb}`}
                        alt={p.name}
                        loading="lazy"
                        className="aspect-[4/5] w-full rounded-md border border-border bg-[#FAF8F2] object-contain group-hover:opacity-90"
                      />
                    )}
                    <span className="mt-0.5 block text-[11px] leading-tight underline-offset-2 group-hover:underline">{p.name}</span>
                    {p.kind !== "exakt" && <span className="block text-[10px] text-muted-foreground">gleiche Gattung</span>}
                  </a>
                ))}
              </div>
            </div>
          ) : (
            <div className="text-xs text-muted-foreground">
              Noch nicht in unserer Bibliothek · <FlowerLinks text={record.national_flower} label="Wikipedia" />
            </div>
          )}
        </div>
      )}
    </AsideSection>
  );
};
